/**
 * Tiny semver helpers used by NativeScript apps and shared utilities.
 *
 * Why this exists: the `semver` npm package is CommonJS and performs
 * many internal `require()` calls into its own `internal/`,
 * `functions/`, `ranges/`, and `classes/` sub-paths (see
 * `node_modules/semver/index.js`). When NS-vite HMR serves modules
 * over HTTP, the CJS shim cannot synchronously resolve those HTTP
 * `require()` calls, so each one logs a `[ns-hmr][require-guard]`
 * warning and returns an empty object as a stub. Callers that read
 * a value off the stub at runtime hit `undefined` silently.
 *
 * We use only `valid()`, `coerce()`, and `lt()` across the workspace
 * (`strings.ts:normalizeVersionString` for the first two,
 * `app-ns.service.ts` for the third). All three can be implemented
 * inline in a few lines without pulling in the runtime dependency.
 * Keeping the helpers behind these named exports lets us swap back
 * to `semver` if/when we need the full grammar.
 */

const SEMVER_RE = /^(\d+)\.(\d+)\.(\d+)(?:-[\w.+-]+)?(?:\+[\w.+-]+)?$/;
const COERCE_RE = /(\d+)(?:\.(\d+))?(?:\.(\d+))?/;

/**
 * Returns the version string when `value` is a strictly valid semver
 * (`major.minor.patch` with optional prerelease/build), otherwise
 * `null`. Mirrors the subset of `semver.valid()` that we exercise —
 * no loose-mode coercion, no implicit `v`-prefix stripping.
 */
export function semverValid(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  return SEMVER_RE.test(value) ? value : null;
}

/**
 * Extract the first version-like prefix from `value` and pad missing
 * parts with zeros. Returns `{ version: 'major.minor.patch' }` so the
 * shape matches the `semver.coerce()` SemVer instance enough for our
 * `.version` reads. Returns `null` if no leading digit is found.
 */
export function semverCoerce(value: unknown): { version: string } | null {
  if (typeof value !== 'string') {
    return null;
  }
  const match = value.match(COERCE_RE);
  if (!match) {
    return null;
  }
  const major = match[1] ?? '0';
  const minor = match[2] ?? '0';
  const patch = match[3] ?? '0';
  return { version: `${major}.${minor}.${patch}` };
}

/**
 * Compare two semver strings: returns `true` when `a < b`. Coerces
 * both inputs first so callers can pass partial versions like `1.0`
 * (matching `semver.lt()`'s tolerant behavior in our usage). Returns
 * `false` on uncomparable inputs — the existing call site
 * (`app-ns.service.ts`) treats "can't compare" as "no upgrade
 * available", which preserves the current safe-default behavior.
 */
export function semverLt(a: unknown, b: unknown): boolean {
  const ca = semverCoerce(a);
  const cb = semverCoerce(b);
  if (!ca || !cb) {
    return false;
  }
  const partsA = ca.version.split('.').map((part) => parseInt(part, 10));
  const partsB = cb.version.split('.').map((part) => parseInt(part, 10));
  for (let i = 0; i < 3; i += 1) {
    const av = partsA[i] ?? 0;
    const bv = partsB[i] ?? 0;
    if (av !== bv) {
      return av < bv;
    }
  }
  return false;
}
